import React, { useState, useEffect } from 'react';
import { Card, Table, Tag, Button, Modal, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { orderAPI } from '../../utils/api';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [detailModal, setDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, [pagination.current]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await orderAPI.getOrdersWithPagination(
        pagination.current,
        pagination.pageSize
      );
      
      if (response.success) {
        setOrders(response.data);
        setPagination({
          current: response.pagination.current,
          pageSize: response.pagination.pageSize,
          total: response.pagination.total
        });
      }
    } catch (error) {
      message.error('加载订单失败');
    } finally {
      setLoading(false);
    }
  };

  const showDetail = (order) => {
    setSelectedOrder(order);
    setDetailModal(true);
  };

  const getStatusTag = (status) => {
    const statusMap = {
      pending: { text: '待支付', color: 'warning' },
      paid: { text: '已支付', color: 'processing' },
      completed: { text: '已完成', color: 'success' },
      cancelled: { text: '已取消', color: 'default' },
      refunded: { text: '已退款', color: 'error' }
    };
    const config = statusMap[status] || { text: status, color: 'default' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 200,
      ellipsis: true
    },
    {
      title: '用户',
      dataIndex: ['user', 'username'],
      key: 'username',
      width: 120,
      render: (username) => username || '游客'
    },
    {
      title: '商品',
      dataIndex: 'productName',
      key: 'productName',
      width: 150,
      ellipsis: true
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 70,
      align: 'center'
    },
    {
      title: '金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 100,
      render: (amount) => `¥${amount || 0}`
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: '卡密',
      dataIndex: 'deliveryInfo',
      key: 'deliveryInfo',
      width: 200,
      ellipsis: true,
      render: (info) => info ? (
        <span style={{ fontSize: '12px', color: '#1890ff', cursor: 'pointer' }} title={info}>
          {info.length > 30 ? info.substring(0, 30) + '...' : info}
        </span>
      ) : <span style={{ color: '#999' }}>-</span>
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (time) => new Date(time).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showDetail(record)}
        >
          查看
        </Button>
      )
    }
  ];

  return (
    <div>
      <Card title="订单管理">
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: (page) => setPagination({ ...pagination, current: page })
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title="订单详情"
        open={detailModal}
        onCancel={() => setDetailModal(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModal(false)}>
            关闭
          </Button>
        ]}
        width={700}
      >
        {selectedOrder && (
          <div>
            <p><strong>订单号:</strong> {selectedOrder.orderNo}</p>
            <p><strong>用户:</strong> {selectedOrder.user?.username || '游客'} ({selectedOrder.user?.email})</p>
            <p><strong>商品:</strong> {selectedOrder.productName}</p>
            <p><strong>数量:</strong> {selectedOrder.quantity}</p>
            <p><strong>金额:</strong> ¥{selectedOrder.totalAmount || 0}</p>
            <p><strong>状态:</strong> {getStatusTag(selectedOrder.status)}</p>
            <p><strong>支付方式:</strong> {selectedOrder.paymentMethod}</p>
            {selectedOrder.transactionId && (
              <p><strong>交易号:</strong> {selectedOrder.transactionId}</p>
            )}
            <p><strong>创建时间:</strong> {new Date(selectedOrder.createdAt).toLocaleString('zh-CN')}</p>
            {selectedOrder.paidAt && (
              <p><strong>支付时间:</strong> {new Date(selectedOrder.paidAt).toLocaleString('zh-CN')}</p>
            )}
            {selectedOrder.contactEmail && (
              <p><strong>联系邮箱:</strong> {selectedOrder.contactEmail}</p>
            )}
            {selectedOrder.deliveryInfo && (
              <div style={{ marginTop: 16, padding: 12, background: '#e6f7ff', borderRadius: 4, border: '1px solid #91d5ff' }}>
                <strong style={{ color: '#0050b3' }}>发货卡密:</strong>
                <div style={{ marginTop: 8, padding: 8, background: '#fff', borderRadius: 4, fontFamily: 'monospace', fontSize: '13px', wordBreak: 'break-all' }}>
                  {selectedOrder.deliveryInfo}
                </div>
              </div>
            )}
            {selectedOrder.deliveryContent && (
              <div style={{ marginTop: 16 }}>
                <strong>发货内容:</strong>
                <pre style={{ background: '#f5f5f5', padding: 12, borderRadius: 4, marginTop: 8 }}>
                  {selectedOrder.deliveryContent}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Orders;
